// errorHandler.ts
import { Platform } from "react-native";
import { trackAppError, flushPostHogBestEffort } from "./analytics/analyticsUtil";
import { useErrorStore } from "./store/errorStore";
import { setJSExceptionHandler, setNativeExceptionHandler } from "react-native-exception-handler";

// Keep a reference to the original console.error
const originalConsoleError = console.error;

let isHandlingConsoleError = false;
let lastErrorSignature: string | null = null;
let lastErrorTimestamp = 0;
const DEDUPE_WINDOW = 2000; // ms

function shouldTrack(error: Error): boolean {
  const signature = `${error.message}-${error.stack}`;
  const now = Date.now();

  if (lastErrorSignature === signature && now - lastErrorTimestamp < DEDUPE_WINDOW) {
    // duplicate within window → skip tracking
    return false;
  }

  lastErrorSignature = signature;
  lastErrorTimestamp = now;
  return true;
}
if (Platform.OS !== 'web') {
  setJSExceptionHandler((error, isFatal) => {
    console.error("Unhandled error:", error);
    if (shouldTrack(error)) {
      trackAppError({
        message: error.message,
        stack: error.stack,
        isFatal,
        source: "JSExceptionHandler",
      });
      if (isFatal) flushPostHogBestEffort();
      useErrorStore.setState({ error });
    }
  });

  setNativeExceptionHandler((errorString) => {
    console.error("Native Exception:", errorString);
    const error = new Error(errorString);
    if (shouldTrack(error)) {
      trackAppError({
        message: errorString,
        source: "NativeExceptionHandler",
        isFatal: true,
      });
       flushPostHogBestEffort();
      useErrorStore.setState({ error });
    }
  });
}
export function setupGlobalErrorHandlers() {
  // Patch console.error safely
  console.error = (...args) => {
    if (
      typeof args[0] === "string" &&
      args[0].includes("useInsertionEffect must not schedule updates")
    ) {
      return; // suppress warning
    }
    if (isHandlingConsoleError) {
      return originalConsoleError(...args); // avoid recursion
    }

    try {
      isHandlingConsoleError = true;

      const error =
        args[0] instanceof Error ? args[0] : new Error(String(args[0]));

      if (shouldTrack(error)) {
        trackAppError({
          message: error.message,
          stack: error.stack,
          args: args.map(String),
          source: "console.error",
        });
        useErrorStore.setState({ error });
      }
    } catch (e) {
      originalConsoleError("Error in console.error handler", e);
    } finally {
      isHandlingConsoleError = false;
    }

    originalConsoleError(...args); // always log to native console
  };

  if (Platform.OS !== 'web') {
    // Catch runtime errors
    ErrorUtils.setGlobalHandler((error: any, isFatal?: boolean) => {
      console.log("Global JS Error:", error, { isFatal });
      if (error instanceof Error && shouldTrack(error)) {
        useErrorStore.setState({ error });
        trackAppError({
          message: error.message,
          stack: error.stack,
          isFatal,
          source: "ErrorUtils",
        });
         if (isFatal) flushPostHogBestEffort();
      }
    });
  }
  // Catch unhandled promise rejections
  if (typeof globalThis.addEventListener === "function") {
    globalThis.addEventListener("unhandledrejection", (event) => {
      const reason =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));

      console.error("Unhandled Promise Rejection:", event.reason);

      if (shouldTrack(reason)) {
        useErrorStore.setState({ error: reason });
        trackAppError({
          message: reason.message,
          stack: reason.stack,
          source: "unhandledrejection",
        });
      }
    });
  }
}
